// RemovePlayerModal — B12 confirm dialog for the PlayerEdit danger zone.
//
// Copy branches on the player's source, mirroring the backend's
// DELETE /api/players/:id semantics:
//   source='agent'      → permanent hard delete (cascades everything)
//   source='api_sports' → detach back to the Discover pool
//
// The modal owns pending/error state; the caller owns the DELETE call via
// onConfirm (so PlayerEdit controls navigation after success). Focus trap +
// Escape + focus restore follow the OutreachModal A4 pattern.

import { useEffect, useRef, useState } from 'react'

interface Props {
  open: boolean
  playerName: string
  // true → hard delete copy; false → detach copy.
  isAgentCreated: boolean
  onClose: () => void
  // Resolves on success (caller navigates away); throws with a message to
  // surface inline on failure.
  onConfirm: () => Promise<void>
}

export default function RemovePlayerModal({
  open,
  playerName,
  isAgentCreated,
  onClose,
  onConfirm,
}: Props) {
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const previouslyFocusedRef = useRef<HTMLElement | null>(null)
  const dialogRef = useRef<HTMLDivElement | null>(null)
  const cancelRef = useRef<HTMLButtonElement | null>(null)

  // Reset on every open; focus lands on Cancel (the safe action for a
  // destructive dialog) and returns to the trigger on close.
  useEffect(() => {
    if (!open) return
    previouslyFocusedRef.current = (document.activeElement as HTMLElement) ?? null
    setError(null)
    setPending(false)
    const t = window.setTimeout(() => cancelRef.current?.focus(), 0)
    return () => {
      window.clearTimeout(t)
      previouslyFocusedRef.current?.focus?.()
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !pending) {
        onClose()
        return
      }
      if (e.key === 'Tab') {
        const root = dialogRef.current
        if (!root) return
        const focusables = root.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
        )
        if (focusables.length === 0) return
        const first = focusables[0]
        const last = focusables[focusables.length - 1]
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, pending, onClose])

  if (!open) return null

  async function confirm(): Promise<void> {
    if (pending) return
    setPending(true)
    setError(null)
    try {
      await onConfirm()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Request failed')
      setPending(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
      onClick={() => {
        if (!pending) onClose()
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="remove-player-modal-title"
    >
      <div
        ref={dialogRef}
        className="bg-hoop-surface border border-hoop-border rounded-2xl shadow-xl w-full max-w-lg p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          id="remove-player-modal-title"
          className="text-lg font-semibold text-hoop-text"
        >
          {isAgentCreated
            ? `Permanently delete ${playerName}?`
            : `Remove ${playerName} from your roster?`}
        </h2>
        <p className="mt-2 text-sm text-hoop-text-2">
          {isAgentCreated
            ? 'This removes their profile, stats, references, and saved entries for every user. Cannot be undone.'
            : 'Their profile returns to Discover and is no longer managed by you.'}
        </p>

        {error && (
          <div className="mt-3 border border-hoop-red-bg bg-hoop-red-bg text-hoop-red rounded-md px-3 py-2 text-xs">
            {error}
          </div>
        )}

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            ref={cancelRef}
            type="button"
            onClick={onClose}
            disabled={pending}
            className="px-4 py-2 text-sm rounded-md border border-hoop-border text-hoop-text-2 hover:bg-hoop-bg disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void confirm()}
            disabled={pending}
            className="px-4 py-2 text-sm rounded-md bg-hoop-red text-white hover:opacity-90 disabled:opacity-40"
          >
            {pending
              ? 'Removing…'
              : isAgentCreated
                ? 'Delete permanently'
                : 'Remove from roster'}
          </button>
        </div>
      </div>
    </div>
  )
}
